import { useActorRef, useSelector } from '@xstate/react';
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import type { ActorRefFrom, SnapshotFrom } from 'xstate';
import { UserContext } from '~/app';
import type { FeeAuthorization } from '~/features/employer/billing/types';
import { API_CALLS as b2bApi } from '~/features/market/job-opportunities/api';
import { API_CALLS as jobApi } from '~/features/market/job-responses/provider-responses/api';
import type { ProviderJobMessage } from '~/features/market/job-responses/provider-responses/types';
import { MARKET_READ_TIMEOUT_MS } from '~/features/market/requestPolicy';
import { buildB2BOpportunitiesUrl, buildJobOpportunitiesUrl } from '~/routing';
import { withRequestDeadline } from '~/utils/requestDeadline';
import { selectUnmaskViewerScope } from './market-content/ResponseUnmaskPanel';
import {
  receivedResponseActionsMachine,
  receivedResponseKey,
  type ReceivedResponseAction,
  type ReceivedResponseScope,
} from './receivedResponseActions.machine';

type Snapshot = SnapshotFrom<typeof receivedResponseActionsMachine>;
const Context = createContext<ActorRefFrom<typeof receivedResponseActionsMachine> | null>(null);
type VisibleResponseOwner = { scope: ReceivedResponseScope; token: symbol };
const VisibleResponseContext = createContext<Dispatch<
  SetStateAction<VisibleResponseOwner[]>
> | null>(null);
const selectBusy = (s: Snapshot) => !s.matches('ready');
const selectPending = (s: Snapshot) => s.context.pending;
const selectUncertain = (s: Snapshot) => s.context.uncertain;
const selectRefreshed = (s: Snapshot) => s.context.refreshed;
const selectReadError = (s: Snapshot) => s.context.readError;
const selectReceipt = (s: Snapshot) => s.context.receipt;

const receivedResponseUrl = (scope: ReceivedResponseScope) =>
  scope.workspace === 'job'
    ? buildJobOpportunitiesUrl({ responseId: scope.responseId })
    : buildB2BOpportunitiesUrl({
        view: 'received',
        responseId: scope.responseId,
        side: scope.side,
      });

const RecoveryLink = ({
  actor,
  visibleScopes,
}: {
  actor: ActorRefFrom<typeof receivedResponseActionsMachine>;
  visibleScopes: ReceivedResponseScope[];
}) => {
  const pending = useSelector(actor, selectPending);
  const uncertain = useSelector(actor, selectUncertain);
  const readError = useSelector(actor, selectReadError);
  const action = uncertain ?? pending;
  if (!action) return null;
  return (
    <RecoveryNotice
      action={action}
      visibleScopes={visibleScopes}
      message={
        uncertain
          ? 'A received response action has an unconfirmed outcome.'
          : readError
            ? 'A received response action succeeded, but its refreshed details are unavailable.'
            : 'A received response action is still being checked.'
      }
    />
  );
};

const RecoveryNotice = ({
  action,
  message,
  visibleScopes,
}: {
  action: ReceivedResponseScope;
  message: string;
  visibleScopes: ReceivedResponseScope[];
}) => {
  const location = useLocation();
  const target = receivedResponseUrl(action);
  const query = new URLSearchParams(location.search);
  const viewingOriginal =
    location.pathname === target.split('?')[0] &&
    (query.get('responseId') === String(action.responseId) ||
      visibleScopes.some(
        (visibleScope) =>
          visibleScope.workspace === action.workspace &&
          visibleScope.responseId === action.responseId &&
          visibleScope.listingId === action.listingId
      )) &&
    (action.workspace === 'job' || query.get('view') !== 'sent');
  if (viewingOriginal) return null;
  return (
    <div className="m-4 space-y-2">
      <p role="status">{message}</p>
      <Link className="text-primary underline" to={target}>
        Review received {action.workspace === 'job' ? 'JOB' : 'B2B'} response {action.responseId}
      </Link>
    </div>
  );
};

const Owner = ({ children, recoveryLinks }: { children: ReactNode; recoveryLinks: boolean }) => {
  const actor = useActorRef(receivedResponseActionsMachine);
  const [scopes, setScopes] = useState<VisibleResponseOwner[]>([]);
  return (
    <Context.Provider value={actor}>
      <VisibleResponseContext.Provider value={setScopes}>
        {recoveryLinks && (
          <RecoveryLink actor={actor} visibleScopes={scopes.map(({ scope }) => scope)} />
        )}
        {children}
      </VisibleResponseContext.Provider>
    </Context.Provider>
  );
};

/** The HLX layout retains operations across routes; standalone views own a local boundary. */
export const ReceivedResponseActionsBoundary = ({
  children,
  recoveryLinks = false,
}: {
  children: ReactNode;
  recoveryLinks?: boolean;
}) => {
  const parent = useContext(Context);
  const viewer = useSelector(UserContext.useActorRef(), selectUnmaskViewerScope);
  return parent ? (
    children
  ) : (
    <Owner key={viewer} recoveryLinks={recoveryLinks}>
      {children}
    </Owner>
  );
};

export const useReceivedResponseActions = (scope: ReceivedResponseScope | null) => {
  const actor = useContext(Context);
  const visibleResponse = useContext(VisibleResponseContext);
  if (!actor) throw new Error('Received response actions require their viewer boundary.');
  const navigate = useNavigate();
  const key = scope ? receivedResponseKey(scope) : '';
  const workspace = scope?.workspace;
  const responseId = scope?.responseId;
  const listingId = scope?.listingId;
  const busy = useSelector(actor, selectBusy);
  const pending = useSelector(actor, selectPending);
  const uncertain = useSelector(actor, selectUncertain);
  const refreshed = useSelector(actor, selectRefreshed);
  const readError = useSelector(actor, selectReadError);
  const receipt = useSelector(actor, selectReceipt);
  const draft = useSelector(actor, (s) => s.context.drafts[key] ?? '');
  const refusal = useSelector(actor, (s) => s.context.errors[key] ?? null);
  const refusedReply = useSelector(actor, (s) => s.context.errorKinds[key] === 'reply');
  const feeRefusal = useSelector(actor, (s) => s.context.feeRefusals[key] ?? null);
  // [MCV-1933] Which accept was refused (an offer or an application on the listing's terms) and the
  // schedule the server attached to the refusal.
  const feeRefusalKind = useSelector(actor, (s) =>
    s.context.feeRefusals[key] ? (s.context.errorKinds[key] ?? null) : null
  );
  const feeRefusalSchedule = useSelector(actor, (s) => s.context.feeRefusalSchedules[key] ?? null);
  const error = refusedReply ? null : refusal;
  const replyError = refusedReply ? refusal : null;
  const [reloadNonce, setReloadNonce] = useState(0);
  const [conversation, setConversation] = useState<{
    key: string;
    messages: ProviderJobMessage[];
    loading: boolean;
    error: string | null;
    reviewRevision: number | null;
  }>({ key: '', messages: [], loading: false, error: null, reviewRevision: null });
  const lastReadKey = useRef('');
  const lastAdoptedRevision = useRef(0);
  const visibleScopeToken = useRef(Symbol('visible received response'));

  useEffect(() => {
    if (workspace === undefined || listingId === undefined || responseId === undefined) return;
    visibleResponse?.((current) => [
      ...current.filter(({ token }) => token !== visibleScopeToken.current),
      { scope: { workspace, listingId, responseId }, token: visibleScopeToken.current },
    ]);
    return () =>
      visibleResponse?.((current) =>
        current.filter(({ token }) => token !== visibleScopeToken.current)
      );
  }, [visibleResponse, workspace, listingId, responseId]);

  useEffect(() => {
    if (
      !key ||
      responseId === undefined ||
      workspace === undefined ||
      (busy && pending && receivedResponseKey(pending) === key)
    )
      return;
    const readKey = `${key}:${reloadNonce}`;
    if (
      refreshed &&
      receivedResponseKey(refreshed.action) === key &&
      lastAdoptedRevision.current !== refreshed.revision
    ) {
      lastAdoptedRevision.current = refreshed.revision;
      lastReadKey.current = readKey;
      setConversation({
        key,
        messages: refreshed.messages,
        loading: false,
        error: null,
        reviewRevision: refreshed.revision,
      });
      return;
    }
    if (readError && pending && receivedResponseKey(pending) === key) {
      setConversation((current) => ({
        key,
        messages: current.key === key ? current.messages : [],
        loading: false,
        error: readError,
        reviewRevision: null,
      }));
      return;
    }
    if (lastReadKey.current === readKey) return;
    lastReadKey.current = readKey;
    const controller = new AbortController();
    let settled = false;
    setConversation((current) => ({
      key,
      messages: current.key === key ? current.messages : [],
      loading: true,
      error: null,
      reviewRevision: null,
    }));
    const api = workspace === 'job' ? jobApi : b2bApi;
    void withRequestDeadline(
      (signal) => api.getMessages(responseId, signal),
      MARKET_READ_TIMEOUT_MS,
      controller.signal
    )
      .then((messages) => {
        if (!controller.signal.aborted)
          setConversation({ key, messages, loading: false, error: null, reviewRevision: null });
      })
      .catch((err: unknown) => {
        if (!controller.signal.aborted)
          setConversation({
            key,
            messages: [],
            loading: false,
            error: err instanceof Error ? err.message : 'Failed to load messages.',
            reviewRevision: null,
          });
      })
      .finally(() => {
        settled = true;
      });
    return () => {
      controller.abort();
      if (!settled) lastReadKey.current = '';
    };
  }, [key, responseId, workspace, reloadNonce, busy, refreshed, readError, pending]);

  const scopedConversation = conversation.key === key ? conversation : null;
  const acknowledgedMessage =
    receipt && receivedResponseKey(receipt.action) === key ? receipt.message : undefined;
  const loadedMessages = scopedConversation?.messages ?? [];
  const messages =
    acknowledgedMessage && !loadedMessages.some((message) => message.id === acknowledgedMessage.id)
      ? [...loadedMessages, acknowledgedMessage]
      : loadedMessages;
  const originalBusy = busy && pending !== null && receivedResponseKey(pending) === key;
  const messagesLoading =
    !originalBusy && Boolean(scope) && (!scopedConversation || scopedConversation.loading);
  const messagesError = scopedConversation?.error ?? null;
  const blocked = busy || Boolean(uncertain) || Boolean(readError);
  const review = () => {
    const original = uncertain ?? pending;
    if (!original || busy) return;
    navigate(receivedResponseUrl(original));
    actor.send({ type: 'REVIEW' });
  };
  return {
    busy,
    blocked,
    pending,
    uncertain,
    refreshed,
    receipt,
    readError,
    draft,
    error,
    replyError,
    feeRefusal,
    feeRefusalKind,
    feeRefusalSchedule,
    messages,
    messagesLoading,
    messagesError,
    edit: (body: string) => {
      if (scope) actor.send({ type: 'EDIT', scope, body });
    },
    write: (
      kind: ReceivedResponseAction['kind'],
      messageBody?: string,
      feeAuthorization?: FeeAuthorization
    ) => {
      if (scope)
        actor.send({
          type: 'WRITE',
          action: {
            ...scope,
            kind,
            messageBody,
            ...(feeAuthorization ? { feeAuthorization } : {}),
          },
        });
    },
    reloadMessages: () => {
      if (readError && pending && receivedResponseKey(pending) === key)
        actor.send({ type: 'REVIEW' });
      else setReloadNonce((nonce) => nonce + 1);
    },
    review,
    canAcknowledge:
      !busy &&
      uncertain !== null &&
      scope !== null &&
      receivedResponseKey(scope) === receivedResponseKey(uncertain) &&
      scopedConversation?.reviewRevision != null &&
      scopedConversation?.reviewRevision === refreshed?.revision,
    acknowledge: () => {
      if (scope && scopedConversation?.reviewRevision != null)
        actor.send({ type: 'ACKNOWLEDGE', scope, revision: scopedConversation.reviewRevision });
    },
    discard: () => actor.send({ type: 'DISCARD' }),
    consumeFeeRefusal: () => {
      if (scope) actor.send({ type: 'CONSUME_FEE_REFUSAL', scope });
    },
  };
};
